import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  MODULO_COBROS,
  authConfig,
  fechaHoyInput,
  formatearFecha,
  formatearMoneda,
  manejarErrorSesion,
  nombreInstructor,
  obtenerId,
  obtenerModuloPorNombre,
  obtenerRolNombre,
  tienePermiso,
  toInputDate,
} from "../utils/cobros";

const ESTADOS_RENOVABLES = ["ACTIVA", "POR_VENCER", "GRACIA", "VENCIDA", "PENDIENTE"];

const RegistrarPagos = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const edit = Boolean(id);

  const conceptoQuery = String(searchParams.get("concepto") || "").toUpperCase();
  const suscripcionQuery = searchParams.get("suscripcionId") || "";

  const [instructores, setInstructores] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [suscripciones, setSuscripciones] = useState([]);

  const [conceptoPago, setConceptoPago] = useState(
    ["SUSCRIPCION", "RENOVACION"].includes(conceptoQuery) ? conceptoQuery : "SUSCRIPCION"
  );
  const [suscripcionReferenciaObjectId, setSuscripcionReferenciaObjectId] = useState(suscripcionQuery);
  const [instructorObjectId, setInstructorObjectId] = useState("");
  const [planObjectId, setPlanObjectId] = useState("");
  const [valorPago, setValorPago] = useState("");
  const [moneda, setMoneda] = useState("COP");
  const [fechaPago, setFechaPago] = useState(fechaHoyInput());
  const [metodoPago, setMetodoPago] = useState("TRANSFERENCIA");
  const [referenciaPago, setReferenciaPago] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [estadoPagoActual, setEstadoPagoActual] = useState("");

  const [loading, setLoading] = useState(false);
  const [cargandoBase, setCargandoBase] = useState(false);

  const modulo = useMemo(() => obtenerModuloPorNombre(MODULO_COBROS), []);
  const puedeCrear = useMemo(() => tienePermiso(modulo, "CREAR"), [modulo]);
  const puedeLeer = useMemo(() => tienePermiso(modulo, "LEER"), [modulo]);
  const puedeActualizar = useMemo(() => tienePermiso(modulo, "ACTUALIZAR"), [modulo]);
  const tienePermisoFormulario = edit ? puedeActualizar : puedeCrear;
  const esInstructor = useMemo(() => obtenerRolNombre() === "INSTRUCTOR", []);

  const suscripcionSeleccionada = useMemo(
    () => suscripciones.find((item) => String(item._id) === String(suscripcionReferenciaObjectId)) || null,
    [suscripciones, suscripcionReferenciaObjectId]
  );

  const planSeleccionado = useMemo(
    () => planes.find((item) => String(item._id) === String(planObjectId)) || null,
    [planes, planObjectId]
  );

  const suscripcionesDisponibles = useMemo(() => {
    return suscripciones.filter((suscripcion) => {
      const estado = String(suscripcion.estadoSuscripcion || "").toUpperCase();
      const mismoInstructor = !instructorObjectId || String(obtenerId(suscripcion.instructorObjectId)) === String(instructorObjectId);
      if (!mismoInstructor) return false;
      if (conceptoPago === "SUSCRIPCION") return estado === "PENDIENTE_PAGO";
      return ESTADOS_RENOVABLES.includes(estado);
    });
  }, [conceptoPago, instructorObjectId, suscripciones]);

  const aplicarSuscripcion = (suscripcion, concepto = conceptoPago) => {
    if (!suscripcion) return;
    const instructorId = obtenerId(suscripcion.instructorObjectId);
    const planId = obtenerId(suscripcion.planObjectId);
    setInstructorObjectId(instructorId);
    setPlanObjectId(planId);

    const plan = planes.find((item) => String(item._id) === String(planId));
    if (plan) {
      setValorPago(String(plan.valorPlan ?? ""));
      setMoneda(plan.moneda || "COP");
    }

    if (concepto === "SUSCRIPCION") {
      setObservaciones((actual) => actual || "Pago de suscripción inicial.");
    } else {
      setObservaciones((actual) => actual || "Pago de renovación.");
    }
  };

  useEffect(() => {
    const cargarBase = async () => {
      try {
        setCargandoBase(true);
        const promesas = [
          axios.get("/api/planes/listall", authConfig()),
          axios.get("/api/suscripciones/listall", authConfig()),
        ];
        if (!esInstructor) promesas.push(axios.get("/api/instructores/listall", authConfig()));

        const respuestas = await Promise.all(promesas);
        const listaPlanes = Array.isArray(respuestas[0].data?.planes) ? respuestas[0].data.planes : [];
        const listaSuscripciones = Array.isArray(respuestas[1].data?.suscripciones) ? respuestas[1].data.suscripciones : [];
        const listaInstructores = !esInstructor && Array.isArray(respuestas[2]?.data?.instructores) ? respuestas[2].data.instructores : [];

        setPlanes(listaPlanes);
        setSuscripciones(listaSuscripciones);
        setInstructores(listaInstructores);

        if (!edit && suscripcionQuery) {
          const seleccion = listaSuscripciones.find((item) => String(item._id) === String(suscripcionQuery));
          if (seleccion) {
            const concepto = ["SUSCRIPCION", "RENOVACION"].includes(conceptoQuery)
              ? conceptoQuery
              : String(seleccion.estadoSuscripcion).toUpperCase() === "PENDIENTE_PAGO"
                ? "SUSCRIPCION"
                : "RENOVACION";
            setConceptoPago(concepto);
            setSuscripcionReferenciaObjectId(seleccion._id);
            setInstructorObjectId(obtenerId(seleccion.instructorObjectId));
            setPlanObjectId(obtenerId(seleccion.planObjectId));
            const plan = listaPlanes.find((item) => String(item._id) === String(obtenerId(seleccion.planObjectId)));
            if (plan) {
              setValorPago(String(plan.valorPlan ?? ""));
              setMoneda(plan.moneda || "COP");
            }
            setObservaciones(concepto === "RENOVACION" ? "Pago de renovación." : "Pago de suscripción inicial.");
          }
        }
      } catch (error) {
        console.error("Error cargando datos del pago:", error);
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", "No se pudieron cargar los datos para registrar el pago.", "error");
      } finally {
        setCargandoBase(false);
      }
    };
    cargarBase();
  }, [conceptoQuery, edit, esInstructor, navigate, suscripcionQuery]);

  useEffect(() => {
    if (!id) return;
    const cargarPago = async () => {
      if (!puedeLeer && !puedeActualizar) return;
      try {
        setLoading(true);
        const { data } = await axios.get(`/api/pagos/listid/${id}`, authConfig());
        const pago = data?.pago || data?.message;
        if (!pago) throw new Error("No se pudo cargar el pago.");

        setConceptoPago(pago.conceptoPago || "SUSCRIPCION");
        setSuscripcionReferenciaObjectId(obtenerId(pago.suscripcionReferenciaObjectId));
        setInstructorObjectId(obtenerId(pago.instructorObjectId));
        setPlanObjectId(obtenerId(pago.planObjectId));
        setValorPago(String(pago.valorPago ?? ""));
        setMoneda(pago.moneda || "COP");
        setFechaPago(toInputDate(pago.fechaPago) || fechaHoyInput());
        setMetodoPago(pago.metodoPago || "TRANSFERENCIA");
        setReferenciaPago(pago.referenciaPago || "");
        setObservaciones(pago.observaciones || "");
        setEstadoPagoActual(pago.estadoPago || "");
      } catch (error) {
        console.error("Error cargando pago:", error);
        if (manejarErrorSesion(error, navigate)) return;
        Swal.fire("Error", error.response?.data?.message || error.message, "error");
      } finally {
        setLoading(false);
      }
    };
    cargarPago();
  }, [id, navigate, puedeActualizar, puedeLeer]);

  const cambiarConcepto = (nuevoConcepto) => {
    setConceptoPago(nuevoConcepto);
    setSuscripcionReferenciaObjectId("");
    setInstructorObjectId("");
    setPlanObjectId("");
    setValorPago("");
    setMoneda("COP");
  };

  const cambiarSuscripcion = (suscripcionId) => {
    setSuscripcionReferenciaObjectId(suscripcionId);
    const suscripcion = suscripciones.find((item) => String(item._id) === String(suscripcionId));
    aplicarSuscripcion(suscripcion);
  };

  const cambiarPlanRenovacion = (nuevoPlanId) => {
    setPlanObjectId(nuevoPlanId);
    const plan = planes.find((item) => String(item._id) === String(nuevoPlanId));
    if (plan) {
      setValorPago(String(plan.valorPlan ?? ""));
      setMoneda(plan.moneda || "COP");
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (!tienePermisoFormulario) {
      Swal.fire("Sin permisos", "No tiene permisos para guardar pagos.", "warning");
      return;
    }

    if (!suscripcionReferenciaObjectId || !instructorObjectId || !planObjectId) {
      Swal.fire("Atención", "Debe seleccionar la suscripción relacionada.", "warning");
      return;
    }

    if (!metodoPago || !fechaPago || Number(valorPago) < 0) {
      Swal.fire("Atención", "Método, fecha y valor del pago son obligatorios.", "warning");
      return;
    }

    const payload = {
      conceptoPago,
      suscripcionReferenciaObjectId,
      instructorObjectId,
      planObjectId,
      valorPago: Number(valorPago || 0),
      moneda: String(moneda || "COP").toUpperCase().trim(),
      fechaPago,
      metodoPago,
      referenciaPago: referenciaPago.trim(),
      observaciones: observaciones.trim(),
    };

    try {
      setLoading(true);
      const { data } = edit
        ? await axios.put(`/api/pagos/update/${id}`, payload, authConfig())
        : await axios.post("/api/pagos/add", payload, authConfig());

      if (data?.aprobadoAutomaticamente) {
        await Swal.fire({
          icon: "success",
          title: "Pago en efectivo aprobado",
          text:
            conceptoPago === "RENOVACION"
              ? "El pago quedó aprobado y el nuevo período de renovación fue generado."
              : "El pago quedó aprobado y la suscripción fue activada.",
        });
      } else {
        await Swal.fire({
          icon: "success",
          title: data?.message || (edit ? "Pago actualizado" : "Pago registrado"),
          showConfirmButton: false,
          timer: 1800,
        });
      }

      navigate("/cobros/pagos");
    } catch (error) {
      console.error("Error guardando pago:", error);
      if (manejarErrorSesion(error, navigate)) return;
      Swal.fire({
        icon: "error",
        title: edit ? "Error al actualizar" : "Error al registrar",
        text: error.response?.data?.message || error.message || "No se pudo guardar el pago.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (edit && estadoPagoActual && estadoPagoActual !== "PENDIENTE") {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">Este pago está <strong>{estadoPagoActual}</strong>. Solo los pagos pendientes pueden editarse.</div>
        <button className="btn btn-dark" type="button" onClick={() => navigate("/cobros/pagos")}>Volver</button>
      </div>
    );
  }

  if (!tienePermisoFormulario) {
    return <div className="container py-5"><div className="alert alert-warning">No tiene permisos para {edit ? "actualizar" : "registrar"} pagos.</div></div>;
  }

  return (
    <div className="container-fluid px-2 px-md-4">
      <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mb-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h4 className="mb-1 fw-semibold">{edit ? "Actualizar Pago" : conceptoPago === "RENOVACION" ? "Registrar Pago de Renovación" : "Registrar Pago de Suscripción"}</h4>
            <p className="mb-0 text-secondary small">
              El concepto determina si el pago activa una suscripción pendiente o crea un nuevo período de renovación.
            </p>
          </div>
          <button type="button" className="btn btn-outline-dark" onClick={() => navigate("/cobros/pagos")} disabled={loading}>
            <i className="fa-solid fa-arrow-left me-2"></i>Volver
          </button>
        </div>
      </div>

      <form onSubmit={actions}>
        <div className="row g-3">
          <div className="col-12 col-xl-8">
            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
              <h6 className="fw-semibold">Operación</h6>
              <hr />

              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary text-uppercase">Concepto *</label>
                  <select className="form-select" value={conceptoPago} onChange={(e) => cambiarConcepto(e.target.value)} disabled={edit || Boolean(suscripcionQuery) || loading}>
                    <option value="SUSCRIPCION">Suscripción</option>
                    <option value="RENOVACION">Renovación</option>
                  </select>
                </div>

                {!esInstructor && !suscripcionQuery && !edit && (
                  <div className="col-12 col-md-6">
                    <label className="form-label small text-secondary text-uppercase">Instructor</label>
                    <select className="form-select" value={instructorObjectId} onChange={(e) => { setInstructorObjectId(e.target.value); setSuscripcionReferenciaObjectId(""); }} disabled={cargandoBase || loading}>
                      <option value="">Todos / seleccione</option>
                      {instructores.map((instructor) => (
                        <option key={instructor._id} value={instructor._id}>{nombreInstructor(instructor)} - {instructor.documentoInstructor || "Sin documento"}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="col-12">
                  <label className="form-label small text-secondary text-uppercase">Suscripción relacionada *</label>
                  <select className="form-select" value={suscripcionReferenciaObjectId} onChange={(e) => cambiarSuscripcion(e.target.value)} disabled={edit || Boolean(suscripcionQuery) || cargandoBase || loading} required>
                    <option value="">Seleccione</option>
                    {suscripcionesDisponibles.map((suscripcion) => (
                      <option key={suscripcion._id} value={suscripcion._id}>
                        {nombreInstructor(suscripcion.instructorObjectId)} · {suscripcion.planObjectId?.nombrePlan || "Plan"} · {String(suscripcion.estadoSuscripcion).replaceAll("_", " ")}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    {conceptoPago === "SUSCRIPCION"
                      ? "Solo aparecen suscripciones PENDIENTE DE PAGO."
                      : "Seleccione el período que desea renovar. No se crea una solicitud intermedia."}
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label small text-secondary text-uppercase">Plan *</label>
                  <select className="form-select" value={planObjectId} onChange={(e) => cambiarPlanRenovacion(e.target.value)} disabled={conceptoPago === "SUSCRIPCION" || edit || !suscripcionReferenciaObjectId || loading} required>
                    <option value="">Seleccione</option>
                    {planes.filter((plan) => plan.estadoPlan !== false && plan.permiteRenovacion !== false).map((plan) => (
                      <option key={plan._id} value={plan._id}>{plan.codigoPlan} - {plan.nombrePlan}</option>
                    ))}
                  </select>
                  {conceptoPago === "RENOVACION" && <div className="form-text">En renovación puede mantener o cambiar a otro plan activo.</div>}
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label small text-secondary text-uppercase">Valor *</label>
                  <input type="number" min="0" className="form-control" value={valorPago} onChange={(e) => setValorPago(e.target.value)} disabled={loading} required />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label small text-secondary text-uppercase">Moneda *</label>
                  <input className="form-control" value={moneda} onChange={(e) => setMoneda(e.target.value.toUpperCase())} disabled={loading} required />
                </div>
              </div>
            </div>

            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mt-3">
              <h6 className="fw-semibold">Datos del pago</h6>
              <hr />
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <label className="form-label small text-secondary text-uppercase">Fecha *</label>
                  <input type="date" className="form-control" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} disabled={loading} required />
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small text-secondary text-uppercase">Método *</label>
                  <select className="form-select" value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)} disabled={loading} required>
                    <option value="TRANSFERENCIA">Transferencia</option>
                    <option value="PSE">PSE</option>
                    <option value="TARJETA">Tarjeta</option>
                    {!esInstructor && <option value="EFECTIVO">Efectivo</option>}
                    <option value="CONSIGNACION">Consignación</option>
                    <option value="OTRO">Otro</option>
                  </select>
                </div>
                <div className="col-12 col-md-4">
                  <label className="form-label small text-secondary text-uppercase">Referencia</label>
                  <input className="form-control" value={referenciaPago} onChange={(e) => setReferenciaPago(e.target.value)} disabled={loading} placeholder="Número de transacción" />
                </div>
                <div className="col-12">
                  <label className="form-label small text-secondary text-uppercase">Observaciones</label>
                  <textarea className="form-control" rows="3" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} disabled={loading} />
                </div>
              </div>

              {!esInstructor && metodoPago === "EFECTIVO" && !edit && (
                <div className="alert alert-success mt-3 mb-0">
                  <i className="fa-solid fa-circle-check me-2"></i>
                  Al registrar un pago en efectivo desde administración se aprobará automáticamente.
                </div>
              )}
            </div>
          </div>

          <div className="col-12 col-xl-4">
            <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4">
              <h6 className="fw-semibold">Resumen</h6>
              <hr />
              {suscripcionSeleccionada ? (
                <div className="small">
                  <div className="mb-2"><span className="text-secondary">Instructor:</span><br /><strong>{nombreInstructor(suscripcionSeleccionada.instructorObjectId)}</strong></div>
                  <div className="mb-2"><span className="text-secondary">Estado origen:</span><br /><strong>{String(suscripcionSeleccionada.estadoSuscripcion || "").replaceAll("_", " ")}</strong></div>
                  <div className="mb-2"><span className="text-secondary">Período actual:</span><br /><strong>{formatearFecha(suscripcionSeleccionada.fechaInicio)} → {formatearFecha(suscripcionSeleccionada.fechaFin)}</strong></div>
                  <div className="mb-2"><span className="text-secondary">Concepto:</span><br /><strong>{conceptoPago === "RENOVACION" ? "RENOVACIÓN" : "SUSCRIPCIÓN"}</strong></div>
                  {planSeleccionado && <div className="mb-2"><span className="text-secondary">Valor plan:</span><br /><strong>{formatearMoneda(planSeleccionado.valorPlan, planSeleccionado.moneda)}</strong></div>}
                  {conceptoPago === "SUSCRIPCION" ? (
                    <div className="alert alert-warning mt-3 mb-0">Al aprobar, se activará esta misma suscripción pendiente. No se creará una segunda fila.</div>
                  ) : (
                    <div className="alert alert-info mt-3 mb-0">Al aprobar, se creará exactamente un nuevo período con origen RENOVACIÓN.</div>
                  )}
                </div>
              ) : (
                <div className="text-secondary small">Seleccione la suscripción relacionada para ver el resumen.</div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border rounded-4 shadow-sm p-3 p-md-4 mt-3">
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <button type="submit" className="btn btn-dark w-100 fw-semibold py-2" disabled={loading || cargandoBase || !tienePermisoFormulario}>
                <i className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"} me-2`}></i>
                {loading ? "Guardando…" : edit ? "Guardar cambios" : "Registrar pago"}
              </button>
            </div>
            <div className="col-12 col-md-6">
              <button type="button" className="btn btn-outline-secondary w-100 fw-semibold py-2" onClick={() => navigate("/cobros/pagos")} disabled={loading}>Cancelar</button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RegistrarPagos;
